/**
 * Tier mock-test hub + round detail UI strings — native copy per locale (no English fallback).
 */

type TierSeoLang = 'ja' | 'th' | 'vi' | 'id' | 'ms' | 'pt-BR' | 'my' | 'zh-TW' | 'zh-CN' | 'de' | 'es' | 'fr' | 'ar';

export type TierSeoKeys = {
  roundDetailBreadcrumbMockTest: string;
  roundDetailLearningPoints: string;
  roundDetailRelatedRounds: string;
  roundDetailBackToHub: string;
  recTabTitle: string;
  recBeginner: string;
  recIntermediate: string;
  recAdvanced: string;
  recScore300: string;
  recScore500: string;
  recScore700: string;
  recFullMock: string;
  recTrap: string;
  recWeakness: string;
  recRecommended: string;
  recPointSuffix: string;
  recQuestionSuffix: string;
  recPartLabels: string;
  mockTestHubTitle: string;
  mockTestHubDesc: string;
  mockTestHubH1: string;
  mockTestHubIntro: string;
  mockTestHubWhyTitle: string;
  mockTestHubWhy1: string;
  mockTestHubWhy2: string;
  mockTestHubWhy3: string;
  mockTestHubWhy4: string;
  mockTestHubHowTitle: string;
  mockTestHubHow1: string;
  mockTestHubHow2: string;
  mockTestHubHow3: string;
  mockTestHubHow4: string;
  mockTestHubFaq1Q: string;
  mockTestHubFaq1A: string;
  mockTestHubFaq2Q: string;
  mockTestHubFaq2A: string;
  mockTestHubFaq3Q: string;
  mockTestHubFaq3A: string;
  mockTestHubFaq4Q: string;
  mockTestHubFaq4A: string;
  mockTestHubAllRounds: string;
};

const ja: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: '模試一覧',
  roundDetailLearningPoints: 'この回の学習ポイント',
  roundDetailRelatedRounds: '同じ難易度の他の回',
  roundDetailBackToHub: '← 模試一覧へ',
  recTabTitle: 'おすすめの回',
  recBeginner: '入門（セクション15点目標）',
  recIntermediate: '中級（セクション22点目標）',
  recAdvanced: '上級（セクション28点目標）',
  recScore300: '目標総合 40〜70',
  recScore500: '目標総合 70〜95',
  recScore700: '目標総合 95〜120',
  recFullMock: 'Reading + Listening フルセット',
  recTrap: '引っかけ問題特化',
  recWeakness: '弱点補強',
  recRecommended: 'おすすめ（最新）',
  recPointSuffix: '/30',
  recQuestionSuffix: '問',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'TOEFL無料模試 — 難易度別の回 | Power TOEFL',
  mockTestHubDesc: 'TOEFL iBT対策を難易度別のPower TOEFL練習回で。設問数と構成は運用設定に基づき、AI解説付きです。',
  mockTestHubH1: '🎯 TOEFL無料模試 — 難易度別の回',
  mockTestHubIntro: '各回の設問数と構成は運用設定に基づきます。現在の公式試験の完全再現ではなく、AIが弱点を分析します。',
  mockTestHubWhyTitle: 'Power TOEFLの難易度別模試が選ばれる理由',
  mockTestHubWhy1: 'AIが毎週新問題を生成 — 最新TOEFL iBT形式を反映',
  mockTestHubWhy2: '入門・中級・上級の3段階で今のレベルに合った回を選択',
  mockTestHubWhy3: '回ごとの学習ガイド — 検索エンジンにも読みやすい充実した解説',
  mockTestHubWhy4: '誤答ノート＋弱点分析でReading・Listeningを集中強化',
  mockTestHubHowTitle: '使い方',
  mockTestHubHow1: '1️⃣ 目標スコアに合う難易度タブ（入門/中級/上級）を選ぶ',
  mockTestHubHow2: '2️⃣ 回の詳細ページで学習ポイントと戦略を読んでから受験',
  mockTestHubHow3: '3️⃣ 受験後、AI分析で弱いセクションを確認',
  mockTestHubHow4: '4️⃣ 同じ難易度の別の回で反復練習',
  mockTestHubFaq1Q: '1回の模試は何問ですか？',
  mockTestHubFaq1A: '設問数は回ごとに運用設定に基づいて提供されます。正確な数は各回の詳細ページで確認できます。Speaking・Writingは別の練習ページで提供しています。',
  mockTestHubFaq2Q: '無料で何回まで受験できますか？',
  mockTestHubFaq2A: 'すべての回を無料で受験できます。1日の問題数に上限があり、Pro会員は無制限です。',
  mockTestHubFaq3Q: '難易度はどう分かれていますか？',
  mockTestHubFaq3A: '入門（セクション15点目標）、中級（22点）、上級（28点）の3段階です。各回の詳細ページに難易度別ガイドがあります。',
  mockTestHubFaq4Q: '結果はどこで確認できますか？',
  mockTestHubFaq4A: '提出後、結果ページで推定スコアとセクション別正答率を確認できます。ログインすると記録が保存されます。',
  mockTestHubAllRounds: '模試一覧',
};

const th: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'รายการข้อสอบจำลอง',
  roundDetailLearningPoints: 'จุดเรียนรู้ของรอบนี้',
  roundDetailRelatedRounds: 'รอบระดับใกล้เคียง',
  roundDetailBackToHub: '← กลับไปรายการข้อสอบจำลอง',
  recTabTitle: 'รอบแนะนำ',
  recBeginner: 'เริ่มต้น (เป้า 15/30 ต่อส่วน)',
  recIntermediate: 'กลาง (เป้า 22/30 ต่อส่วน)',
  recAdvanced: 'ขั้นสูง (เป้า 28/30 ต่อส่วน)',
  recScore300: 'เป้ารวม 40–70',
  recScore500: 'เป้ารวม 70–95',
  recScore700: 'เป้ารวม 95–120',
  recFullMock: 'ชุดเต็ม Reading + Listening',
  recTrap: 'โฟกัสกับดัก',
  recWeakness: 'เสริมจุดอ่อน',
  recRecommended: 'แนะนำล่าสุด',
  recPointSuffix: '/30',
  recQuestionSuffix: ' ข้อ',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'TOEFL ฟรี — รอบแยกตามระดับ | Power TOEFL',
  mockTestHubDesc: 'ฝึกทักษะ TOEFL iBT ตามระดับด้วยรอบฝึก Power TOEFL จำนวนข้อและโครงสร้างของแต่ละรอบอิงตามการตั้งค่าปัจจุบัน พร้อมคำอธิบาย AI',
  mockTestHubH1: '🎯 TOEFL ฟรี — ข้อสอบจำลองแยกตามระดับ',
  mockTestHubIntro: 'แต่ละรอบมีจำนวนข้อและโครงสร้างตามการตั้งค่าปัจจุบัน ไม่ใช่รูปแบบข้อสอบทางการปัจจุบันที่ตรงทั้งหมด พร้อม AI วิเคราะห์จุดอ่อน',
  mockTestHubWhyTitle: 'ทำไมต้องข้อสอบจำลองแยกระดับของ Power TOEFL',
  mockTestHubWhy1: 'AI สร้างข้อใหม่ทุกสัปดาห์ — อัปเดตรูปแบบ TOEFL iBT ล่าสุด',
  mockTestHubWhy2: 'เลือกรอบตามระดับ 3 ขั้นที่ตรงกับเป้าหมายของคุณ',
  mockTestHubWhy3: 'คู่มือเรียนเฉพาะแต่ละรอบ — เนื้อหาหนาแน่นสำหรับ SEO และการเรียนรู้',
  mockTestHubWhy4: 'บันทึกข้อผิด + วิเคราะห์จุดอ่อน Reading·Listening',
  mockTestHubHowTitle: 'วิธีใช้งาน',
  mockTestHubHow1: '1️⃣ เลือกแท็บระดับที่ตรงกับเป้าคะแนน',
  mockTestHubHow2: '2️⃣ อ่านจุดเรียนรู้และกลยุทธ์ในหน้ารายละเอียดรอบ แล้วเริ่มสอบ',
  mockTestHubHow3: '3️⃣ หลังสอบ ดู AI วิเคราะห์จุดอ่อนแต่ละส่วน',
  mockTestHubHow4: '4️⃣ ฝึกซ้ำกับรอบอื่นในระดับเดียวกัน',
  mockTestHubFaq1Q: 'หนึ่งรอบมีกี่ข้อ?',
  mockTestHubFaq1A: 'จำนวนข้อของแต่ละรอบอิงตามการตั้งค่าปัจจุบัน ดูจำนวนที่แน่นอนได้ในหน้ารายละเอียดรอบ ส่วน Speaking·Writing อยู่ในหน้าฝึกแยก',
  mockTestHubFaq2Q: 'ฟรีกี่รอบ?',
  mockTestHubFaq2A: 'ทุกรอบฟรี มีจำกัดต่อวัน สมาชิก Pro ไม่จำกัด',
  mockTestHubFaq3Q: 'ระดับความยากแยกอย่างไร?',
  mockTestHubFaq3A: 'เริ่มต้น (เป้า 15/ส่วน) กลาง (22) ขั้นสูง (28) แต่ละหน้ารอบมีคู่มือตามระดับ',
  mockTestHubFaq4Q: 'ดูผลที่ไหน?',
  mockTestHubFaq4A: 'หลังส่งคำตอบ ดูคะแนนประมาณและความแม่นยำต่อส่วน ล็อกอินเพื่อบันทึกประวัติ',
  mockTestHubAllRounds: 'รายการรอบทั้งหมด',
};

const vi: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'Danh sách đề thi thử',
  roundDetailLearningPoints: 'Điểm học của đợt này',
  roundDetailRelatedRounds: 'Các đợt cùng cấp độ',
  roundDetailBackToHub: '← Về danh sách đề thi thử',
  recTabTitle: 'Đợt gợi ý',
  recBeginner: 'Cơ bản (mục tiêu 15/30 mỗi phần)',
  recIntermediate: 'Trung cấp (mục tiêu 22/30)',
  recAdvanced: 'Nâng cao (mục tiêu 28/30)',
  recScore300: 'Mục tiêu tổng 40–70',
  recScore500: 'Mục tiêu tổng 70–95',
  recScore700: 'Mục tiêu tổng 95–120',
  recFullMock: 'Trọn bộ Reading + Listening',
  recTrap: 'Câu bẫy',
  recWeakness: 'Bù điểm yếu',
  recRecommended: 'Gợi ý mới nhất',
  recPointSuffix: '/30',
  recQuestionSuffix: ' câu',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'Thi thử TOEFL miễn phí — theo cấp độ | Power TOEFL',
  mockTestHubDesc: 'Luyện kỹ năng TOEFL iBT theo cấp với các đợt Power TOEFL. Số câu và cấu trúc mỗi đợt theo thiết lập vận hành hiện tại; có giải thích AI.',
  mockTestHubH1: '🎯 Thi thử TOEFL miễn phí — theo cấp độ',
  mockTestHubIntro: 'Mỗi đợt có số câu và cấu trúc theo thiết lập vận hành hiện tại, không phải định dạng chính thức hiện tại chính xác; AI phân tích điểm yếu.',
  mockTestHubWhyTitle: 'Vì sao chọn đề thi thử theo cấp của Power TOEFL',
  mockTestHubWhy1: 'AI tạo đề mới mỗi tuần — bám sát TOEFL iBT mới nhất',
  mockTestHubWhy2: '3 cấp độ phù hợp mục tiêu điểm của bạn',
  mockTestHubWhy3: 'Hướng dẫn học riêng từng đợt — nội dung dày cho SEO và học tập',
  mockTestHubWhy4: 'Sổ sai + phân tích yếu Reading·Listening',
  mockTestHubHowTitle: 'Cách sử dụng',
  mockTestHubHow1: '1️⃣ Chọn tab cấp độ phù hợp mục tiêu',
  mockTestHubHow2: '2️⃣ Đọc điểm học và chiến lược ở trang chi tiết rồi làm bài',
  mockTestHubHow3: '3️⃣ Sau bài thi, xem AI phân tích phần yếu',
  mockTestHubHow4: '4️⃣ Lặp lại với đợt khác cùng cấp',
  mockTestHubFaq1Q: 'Một đợt có bao nhiêu câu?',
  mockTestHubFaq1A: 'Số câu được thiết lập theo từng đợt. Xem trang chi tiết đợt để biết số câu chính xác. Speaking·Writing ở trang luyện riêng.',
  mockTestHubFaq2Q: 'Miễn phí bao nhiêu đợt?',
  mockTestHubFaq2A: 'Tất cả đợt đều miễn phí, có giới hạn mỗi ngày. Pro không giới hạn.',
  mockTestHubFaq3Q: 'Phân cấp độ thế nào?',
  mockTestHubFaq3A: 'Cơ bản (15/ phần), trung cấp (22), nâng cao (28). Mỗi trang đợt có hướng dẫn theo cấp.',
  mockTestHubFaq4Q: 'Xem kết quả ở đâu?',
  mockTestHubFaq4A: 'Sau nộp bài, trang kết quả hiển thị điểm ước tính và tỷ lệ đúng. Đăng nhập để lưu lịch sử.',
  mockTestHubAllRounds: 'Danh sách đợt thi',
};

const id: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'Daftar simulasi',
  roundDetailLearningPoints: 'Poin belajar putaran ini',
  roundDetailRelatedRounds: 'Putaran tingkat serupa',
  roundDetailBackToHub: '← Kembali ke daftar simulasi',
  recTabTitle: 'Putaran rekomendasi',
  recBeginner: 'Pemula (target 15/30 per bagian)',
  recIntermediate: 'Menengah (target 22/30)',
  recAdvanced: 'Lanjutan (target 28/30)',
  recScore300: 'Target total 40–70',
  recScore500: 'Target total 70–95',
  recScore700: 'Target total 95–120',
  recFullMock: 'Set penuh Reading + Listening',
  recTrap: 'Fokus jebakan',
  recWeakness: 'Perbaiki kelemahan',
  recRecommended: 'Rekomendasi terbaru',
  recPointSuffix: '/30',
  recQuestionSuffix: ' soal',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'Simulasi TOEFL gratis — per tingkat | Power TOEFL',
  mockTestHubDesc: 'Latih keterampilan TOEFL iBT per tingkat dengan putaran Power TOEFL. Jumlah soal dan komposisi mengikuti pengaturan operasional saat ini, dengan pembahasan AI.',
  mockTestHubH1: '🎯 Simulasi TOEFL gratis — per tingkat kesulitan',
  mockTestHubIntro: 'Setiap putaran memiliki jumlah soal dan komposisi sesuai pengaturan operasional saat ini, bukan format resmi saat ini secara persis, dengan analisis AI.',
  mockTestHubWhyTitle: 'Mengapa simulasi bertingkat Power TOEFL',
  mockTestHubWhy1: 'AI menghasilkan soal baru setiap minggu — format TOEFL iBT terbaru',
  mockTestHubWhy2: 'Tiga tingkat sesuai target skor Anda',
  mockTestHubWhy3: 'Panduan belajar per putaran — konten kaya untuk belajar dan SEO',
  mockTestHubWhy4: 'Catatan salah + analisis kelemahan Reading·Listening',
  mockTestHubHowTitle: 'Cara menggunakan',
  mockTestHubHow1: '1️⃣ Pilih tab tingkat sesuai target skor',
  mockTestHubHow2: '2️⃣ Baca poin belajar di halaman putaran lalu mulai ujian',
  mockTestHubHow3: '3️⃣ Setelah ujian, lihat analisis AI bagian lemah',
  mockTestHubHow4: '4️⃣ Ulangi dengan putaran lain di tingkat sama',
  mockTestHubFaq1Q: 'Berapa soal per putaran?',
  mockTestHubFaq1A: 'Jumlah soal ditetapkan per putaran. Lihat halaman detail putaran untuk jumlah yang tepat. Speaking·Writing di halaman latihan terpisah.',
  mockTestHubFaq2Q: 'Berapa putaran gratis?',
  mockTestHubFaq2A: 'Semua putaran gratis dengan batas harian. Pro tanpa batas.',
  mockTestHubFaq3Q: 'Bagaimana tingkat didefinisikan?',
  mockTestHubFaq3A: 'Pemula (15/bagian), menengah (22), lanjutan (28). Setiap halaman putaran punya panduan tingkat.',
  mockTestHubFaq4Q: 'Di mana melihat hasil?',
  mockTestHubFaq4A: 'Setelah submit, halaman hasil menampilkan skor perkiraan dan akurasi per bagian. Login untuk menyimpan riwayat.',
  mockTestHubAllRounds: 'Semua putaran simulasi',
};

const zhTW: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: '模考列表',
  roundDetailLearningPoints: '本回學習重點',
  roundDetailRelatedRounds: '相近難度回次',
  roundDetailBackToHub: '← 返回模考列表',
  recTabTitle: '推薦回次',
  recBeginner: '入門（區塊目標 15 分）',
  recIntermediate: '中級（區塊目標 22 分）',
  recAdvanced: '高級（區塊目標 28 分）',
  recScore300: '目標總分 40–70',
  recScore500: '目標總分 70–95',
  recScore700: '目標總分 95–120',
  recFullMock: 'Reading + Listening 完整套題',
  recTrap: '陷阱題強化',
  recWeakness: '弱點補強',
  recRecommended: '最新推薦',
  recPointSuffix: '/30',
  recQuestionSuffix: ' 題',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'TOEFL 免費模考 — 依難度分回 | Power TOEFL',
  mockTestHubDesc: '依難度以 Power TOEFL 回次訓練 TOEFL iBT 技能；每回題數與組成依目前營運設定提供，附 AI 解析。',
  mockTestHubH1: '🎯 TOEFL 免費模考 — 依難度分回',
  mockTestHubIntro: '每回題數與組成依目前營運設定提供；並非目前官方考試的精確格式，AI 分析弱點。',
  mockTestHubWhyTitle: '為何選擇 Power TOEFL 分級模考',
  mockTestHubWhy1: 'AI 每週生成新題 — 反映最新 TOEFL iBT 題型',
  mockTestHubWhy2: '三階難度對應你的目標分數',
  mockTestHubWhy3: '每回專屬學習指南 — 內容豐富、利於學習與搜尋',
  mockTestHubWhy4: '錯題本 + 弱點分析強化 Reading·Listening',
  mockTestHubHowTitle: '如何使用',
  mockTestHubHow1: '1️⃣ 選擇符合目標的分級標籤',
  mockTestHubHow2: '2️⃣ 在回次詳情閱讀學習重點後開始測驗',
  mockTestHubHow3: '3️⃣ 測驗後查看 AI 弱點分析',
  mockTestHubHow4: '4️⃣ 同難度其他回次反覆練習',
  mockTestHubFaq1Q: '一回有幾題？',
  mockTestHubFaq1A: '題數依各回次營運設定提供，請在回次詳情頁查看實際題數。Speaking·Writing 在獨立練習頁。',
  mockTestHubFaq2Q: '免費可做幾回？',
  mockTestHubFaq2A: '全部回次免費，每日有題數限制。Pro 會員無限制。',
  mockTestHubFaq3Q: '難度如何分級？',
  mockTestHubFaq3A: '入門（區塊 15 分）、中級（22）、高級（28）。每回詳情頁含分級指南。',
  mockTestHubFaq4Q: '結果在哪裡看？',
  mockTestHubFaq4A: '交卷後在結果頁查看估分與各區塊正確率。登入可保存紀錄。',
  mockTestHubAllRounds: '全部回次列表',
};

const zhCN: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: '模考列表',
  roundDetailLearningPoints: '本回学习重点',
  roundDetailRelatedRounds: '相近难度回次',
  roundDetailBackToHub: '← 返回模考列表',
  recTabTitle: '推荐回次',
  recBeginner: '入门（板块目标 15 分）',
  recIntermediate: '中级（板块目标 22 分）',
  recAdvanced: '高级（板块目标 28 分）',
  recScore300: '目标总分 40–70',
  recScore500: '目标总分 70–95',
  recScore700: '目标总分 95–120',
  recFullMock: 'Reading + Listening 完整套题',
  recTrap: '陷阱题强化',
  recWeakness: '弱点补强',
  recRecommended: '最新推荐',
  recPointSuffix: '/30',
  recQuestionSuffix: ' 题',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'TOEFL 免费模考 — 按难度分回 | Power TOEFL',
  mockTestHubDesc: '按难度使用 Power TOEFL 回次训练 TOEFL iBT 技能；每回题数与结构按当前运营设置提供，附 AI 解析。',
  mockTestHubH1: '🎯 TOEFL 免费模考 — 按难度分回',
  mockTestHubIntro: '每回题数与结构按当前运营设置提供；并非当前官方考试的精确格式，AI 分析弱点。',
  mockTestHubWhyTitle: '为何选择 Power TOEFL 分级模考',
  mockTestHubWhy1: 'AI 每周生成新题 — 反映最新 TOEFL iBT 题型',
  mockTestHubWhy2: '三阶难度对应你的目标分数',
  mockTestHubWhy3: '每回专属学习指南 — 内容丰富、利于学习与搜索',
  mockTestHubWhy4: '错题本 + 弱点分析强化 Reading·Listening',
  mockTestHubHowTitle: '如何使用',
  mockTestHubHow1: '1️⃣ 选择符合目标的分级标签',
  mockTestHubHow2: '2️⃣ 在回次详情阅读学习重点后开始测验',
  mockTestHubHow3: '3️⃣ 测验后查看 AI 弱点分析',
  mockTestHubHow4: '4️⃣ 同难度其他回次反复练习',
  mockTestHubFaq1Q: '一回有几题？',
  mockTestHubFaq1A: '题数按各回次运营设置提供，请在回次详情页查看实际题数。Speaking·Writing 在独立练习页。',
  mockTestHubFaq2Q: '免费可做几回？',
  mockTestHubFaq2A: '全部回次免费，每日有题数限制。Pro 会员无限制。',
  mockTestHubFaq3Q: '难度如何分级？',
  mockTestHubFaq3A: '入门（板块 15 分）、中级（22）、高级（28）。每回详情页含分级指南。',
  mockTestHubFaq4Q: '结果在哪里看？',
  mockTestHubFaq4A: '交卷后在结果页查看估分与各板块正确率。登录可保存记录。',
  mockTestHubAllRounds: '全部回次列表',
};

const ms: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'Senarai simulasi',
  roundDetailLearningPoints: 'Titik pembelajaran pusingan ini',
  roundDetailRelatedRounds: 'Pusingan tahap serupa',
  roundDetailBackToHub: '← Kembali ke senarai simulasi',
  recTabTitle: 'Pusingan disyorkan',
  recBeginner: 'Permulaan (sasaran 15/30 setiap bahagian)',
  recIntermediate: 'Pertengahan (sasaran 22/30)',
  recAdvanced: 'Lanjutan (sasaran 28/30)',
  recScore300: 'Jumlah sasaran 40–70',
  recScore500: 'Jumlah sasaran 70–95',
  recScore700: 'Jumlah sasaran 95–120',
  recFullMock: 'Set penuh Reading + Listening',
  recTrap: 'Fokus soalan perangkap',
  recWeakness: 'Baiki kelemahan',
  recRecommended: 'Disyorkan terkini',
  recPointSuffix: '/30',
  recQuestionSuffix: ' soalan',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'TOEFL percuma — pusingan ikut tahap | Power TOEFL',
  mockTestHubDesc: 'Latih kemahiran TOEFL iBT mengikut tahap dengan pusingan Power TOEFL. Bilangan soalan dan komposisi mengikut tetapan operasi semasa, dengan penjelasan AI.',
  mockTestHubH1: '🎯 Simulasi TOEFL percuma — mengikut tahap',
  mockTestHubIntro: 'Setiap pusingan mempunyai bilangan soalan dan komposisi mengikut tetapan operasi semasa, bukan format rasmi semasa yang tepat, dengan analisis AI.',
  mockTestHubWhyTitle: 'Mengapa simulasi berperingkat Power TOEFL',
  mockTestHubWhy1: 'AI menjana soalan baharu setiap minggu — format TOEFL iBT terkini',
  mockTestHubWhy2: 'Tiga tahap sepadan dengan sasaran skor anda',
  mockTestHubWhy3: 'Panduan belajar setiap pusingan — kandungan kaya',
  mockTestHubWhy4: 'Nota silap + analisis kelemahan Reading·Listening',
  mockTestHubHowTitle: 'Cara guna',
  mockTestHubHow1: '1️⃣ Pilih tab tahap mengikut sasaran skor',
  mockTestHubHow2: '2️⃣ Baca panduan pusingan kemudian mula ujian',
  mockTestHubHow3: '3️⃣ Selepas ujian, lihat analisis AI',
  mockTestHubHow4: '4️⃣ Ulang dengan pusingan lain tahap sama',
  mockTestHubFaq1Q: 'Berapa soalan setiap pusingan?',
  mockTestHubFaq1A: 'Bilangan soalan ditetapkan mengikut pusingan. Rujuk halaman butiran pusingan untuk jumlah tepat. Speaking·Writing di halaman berasingan.',
  mockTestHubFaq2Q: 'Percuma berapa pusingan?',
  mockTestHubFaq2A: 'Semua percuma dengan had harian. Pro tanpa had.',
  mockTestHubFaq3Q: 'Bagaimana tahap ditakrifkan?',
  mockTestHubFaq3A: 'Permulaan (15/bahagian), pertengahan (22), lanjutan (28).',
  mockTestHubFaq4Q: 'Di mana lihat keputusan?',
  mockTestHubFaq4A: 'Selepas hantar, lihat skor anggaran dan ketepatan bahagian. Log masuk untuk simpan sejarah.',
  mockTestHubAllRounds: 'Semua pusingan simulasi',
};

const ptBR: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'Lista de simulados',
  roundDetailLearningPoints: 'Pontos de estudo desta rodada',
  roundDetailRelatedRounds: 'Rodadas de nível similar',
  roundDetailBackToHub: '← Voltar à lista de simulados',
  recTabTitle: 'Rodadas recomendadas',
  recBeginner: 'Iniciante (meta 15/30 por seção)',
  recIntermediate: 'Intermediário (meta 22/30)',
  recAdvanced: 'Avançado (meta 28/30)',
  recScore300: 'Total alvo 40–70',
  recScore500: 'Total alvo 70–95',
  recScore700: 'Total alvo 95–120',
  recFullMock: 'Set completo Reading + Listening',
  recTrap: 'Foco em pegadinhas',
  recWeakness: 'Revisar fraquezas',
  recRecommended: 'Recomendadas recentes',
  recPointSuffix: '/30',
  recQuestionSuffix: ' questões',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'Simulados TOEFL grátis — por nível | Power TOEFL',
  mockTestHubDesc: 'Pratique habilidades TOEFL iBT por nível com rodadas Power TOEFL. Quantidade de questões e composição seguem as configurações operacionais atuais, com explicações por IA.',
  mockTestHubH1: '🎯 Simulados TOEFL grátis — por nível',
  mockTestHubIntro: 'Cada rodada segue as configurações operacionais atuais para quantidade de questões e composição, não o formato oficial atual exato, com análise por IA.',
  mockTestHubWhyTitle: 'Por que os simulados por nível da Power TOEFL',
  mockTestHubWhy1: 'IA gera questões novas toda semana — formato TOEFL iBT atualizado',
  mockTestHubWhy2: 'Três níveis alinhados à sua meta de score',
  mockTestHubWhy3: 'Guia de estudo por rodada — conteúdo rico',
  mockTestHubWhy4: 'Caderno de erros + análise Reading·Listening',
  mockTestHubHowTitle: 'Como usar',
  mockTestHubHow1: '1️⃣ Escolha a aba de nível da sua meta',
  mockTestHubHow2: '2️⃣ Leia o guia da rodada e inicie o teste',
  mockTestHubHow3: '3️⃣ Após o teste, veja a análise por IA',
  mockTestHubHow4: '4️⃣ Repita com outras rodadas do mesmo nível',
  mockTestHubFaq1Q: 'Quantas questões por rodada?',
  mockTestHubFaq1A: 'A quantidade de questões é definida por rodada. Veja a página de detalhes da rodada para o número exato. Speaking·Writing em páginas separadas.',
  mockTestHubFaq2Q: 'Quantas rodadas grátis?',
  mockTestHubFaq2A: 'Todas grátis com limite diário. Pro ilimitado.',
  mockTestHubFaq3Q: 'Como os níveis são definidos?',
  mockTestHubFaq3A: 'Iniciante (15/seção), intermediário (22), avançado (28).',
  mockTestHubFaq4Q: 'Onde ver resultados?',
  mockTestHubFaq4A: 'Após enviar, veja score estimado e acurácia por seção. Login salva histórico.',
  mockTestHubAllRounds: 'Todas as rodadas',
};

const de: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'Mock-Test-Übersicht',
  roundDetailLearningPoints: 'Lernpunkte dieser Runde',
  roundDetailRelatedRounds: 'Ähnliche Schwierigkeitsrunden',
  roundDetailBackToHub: '← Zur Mock-Test-Übersicht',
  recTabTitle: 'Empfohlene Runden',
  recBeginner: 'Einsteiger (Ziel 15/30 pro Abschnitt)',
  recIntermediate: 'Mittelstufe (Ziel 22/30)',
  recAdvanced: 'Fortgeschritten (Ziel 28/30)',
  recScore300: 'Gesamtziel 40–70',
  recScore500: 'Gesamtziel 70–95',
  recScore700: 'Gesamtziel 95–120',
  recFullMock: 'Voller Reading + Listening Satz',
  recTrap: 'Fallenfragen',
  recWeakness: 'Schwächen ausbauen',
  recRecommended: 'Neueste Empfehlungen',
  recPointSuffix: '/30',
  recQuestionSuffix: ' Fragen',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'Kostenlose TOEFL-Mock-Tests — nach Niveau | Power TOEFL',
  mockTestHubDesc: 'Trainieren Sie TOEFL-iBT-Fertigkeiten nach Niveau mit Power-TOEFL-Runden. Fragenzahl und Zusammensetzung folgen den aktuellen Betriebseinstellungen, mit KI-Erklärungen.',
  mockTestHubH1: '🎯 Kostenlose TOEFL-Mock-Tests — nach Niveau',
  mockTestHubIntro: 'Jede Runde folgt den aktuellen Betriebseinstellungen für Fragenzahl und Zusammensetzung, nicht dem exakten aktuellen offiziellen Format, mit KI-Analyse.',
  mockTestHubWhyTitle: 'Warum gestaffelte Mock-Tests bei Power TOEFL',
  mockTestHubWhy1: 'KI erzeugt wöchentlich neue Fragen — aktuelles TOEFL iBT-Format',
  mockTestHubWhy2: 'Drei Niveaus passend zu Ihrer Zielpunktzahl',
  mockTestHubWhy3: 'Lernleitfaden pro Runde — reichhaltiger Inhalt',
  mockTestHubWhy4: 'Fehlerbuch + Schwächenanalyse Reading·Listening',
  mockTestHubHowTitle: 'So nutzen Sie es',
  mockTestHubHow1: '1️⃣ Wählen Sie die passende Niveau-Stufe',
  mockTestHubHow2: '2️⃣ Lesen Sie den Rundenguide, dann starten',
  mockTestHubHow3: '3️⃣ Nach dem Test KI-Analyse prüfen',
  mockTestHubHow4: '4️⃣ Mit anderen Runden gleichen Niveaus wiederholen',
  mockTestHubFaq1Q: 'Wie viele Fragen pro Runde?',
  mockTestHubFaq1A: 'Die Fragenzahl wird pro Runde festgelegt. Die exakte Zahl sehen Sie auf der jeweiligen Rundendetailseite. Speaking·Writing auf separaten Seiten.',
  mockTestHubFaq2Q: 'Wie viele Runden kostenlos?',
  mockTestHubFaq2A: 'Alle kostenlos mit Tageslimit. Pro unbegrenzt.',
  mockTestHubFaq3Q: 'Wie sind die Niveaus definiert?',
  mockTestHubFaq3A: 'Einsteiger (15/Abschnitt), Mittel (22), Fortgeschritten (28).',
  mockTestHubFaq4Q: 'Wo sehe ich Ergebnisse?',
  mockTestHubFaq4A: 'Nach dem Absenden: geschätzte Punktzahl und Genauigkeit pro Abschnitt.',
  mockTestHubAllRounds: 'Alle Mock-Test-Runden',
};

const es: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'Lista de simulacros',
  roundDetailLearningPoints: 'Puntos de aprendizaje de esta ronda',
  roundDetailRelatedRounds: 'Rondas de dificultad similar',
  roundDetailBackToHub: '← Volver a la lista de simulacros',
  recTabTitle: 'Rondas recomendadas',
  recBeginner: 'Principiante (objetivo 15/30 por sección)',
  recIntermediate: 'Intermedio (objetivo 22/30)',
  recAdvanced: 'Avanzado (objetivo 28/30)',
  recScore300: 'Total objetivo 40–70',
  recScore500: 'Total objetivo 70–95',
  recScore700: 'Total objetivo 95–120',
  recFullMock: 'Set completo Reading + Listening',
  recTrap: 'Preguntas trampa',
  recWeakness: 'Reforzar debilidades',
  recRecommended: 'Recomendadas recientes',
  recPointSuffix: '/30',
  recQuestionSuffix: ' preguntas',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'Simulacros TOEFL gratis — por nivel | Power TOEFL',
  mockTestHubDesc: 'Practica habilidades TOEFL iBT por nivel con rondas de Power TOEFL. La cantidad de preguntas y la composición siguen la configuración operativa actual, con explicaciones de IA.',
  mockTestHubH1: '🎯 Simulacros TOEFL gratis — por nivel',
  mockTestHubIntro: 'Cada ronda sigue la configuración operativa actual para cantidad de preguntas y composición; no es el formato oficial actual exacto, con análisis de IA.',
  mockTestHubWhyTitle: 'Por qué los simulacros por nivel de Power TOEFL',
  mockTestHubWhy1: 'IA genera preguntas nuevas cada semana — formato TOEFL iBT actual',
  mockTestHubWhy2: 'Tres niveles según tu puntuación objetivo',
  mockTestHubWhy3: 'Guía de estudio por ronda — contenido completo',
  mockTestHubWhy4: 'Cuaderno de errores + análisis Reading·Listening',
  mockTestHubHowTitle: 'Cómo usarlo',
  mockTestHubHow1: '1️⃣ Elige la pestaña de nivel acorde a tu meta',
  mockTestHubHow2: '2️⃣ Lee la guía de la ronda y empieza el examen',
  mockTestHubHow3: '3️⃣ Tras el examen, revisa el análisis IA',
  mockTestHubHow4: '4️⃣ Repite con otras rondas del mismo nivel',
  mockTestHubFaq1Q: '¿Cuántas preguntas por ronda?',
  mockTestHubFaq1A: 'La cantidad de preguntas se define por ronda. Consulta la página de detalle de cada ronda para el número exacto. Speaking·Writing en páginas aparte.',
  mockTestHubFaq2Q: '¿Cuántas rondas gratis?',
  mockTestHubFaq2A: 'Todas gratis con límite diario. Pro ilimitado.',
  mockTestHubFaq3Q: '¿Cómo se definen los niveles?',
  mockTestHubFaq3A: 'Principiante (15/sección), intermedio (22), avanzado (28).',
  mockTestHubFaq4Q: '¿Dónde veo resultados?',
  mockTestHubFaq4A: 'Tras enviar, puntaje estimado y precisión por sección. Inicia sesión para guardar.',
  mockTestHubAllRounds: 'Todas las rondas',
};

const fr: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'Liste des examens blancs',
  roundDetailLearningPoints: 'Points clés de cette session',
  roundDetailRelatedRounds: 'Sessions de niveau similaire',
  roundDetailBackToHub: '← Retour à la liste des examens blancs',
  recTabTitle: 'Sessions recommandées',
  recBeginner: 'Débutant (objectif 15/30 par section)',
  recIntermediate: 'Intermédiaire (objectif 22/30)',
  recAdvanced: 'Avancé (objectif 28/30)',
  recScore300: 'Total visé 40–70',
  recScore500: 'Total visé 70–95',
  recScore700: 'Total visé 95–120',
  recFullMock: 'Set complet Reading + Listening',
  recTrap: 'Pièges fréquents',
  recWeakness: 'Renforcer les faiblesses',
  recRecommended: 'Recommandations récentes',
  recPointSuffix: '/30',
  recQuestionSuffix: ' questions',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'Examens blancs TOEFL gratuits — par niveau | Power TOEFL',
  mockTestHubDesc: 'Travaillez les compétences TOEFL iBT par niveau avec des sessions Power TOEFL. Le nombre de questions et la composition suivent les paramètres opérationnels actuels, avec explications IA.',
  mockTestHubH1: '🎯 Examens blancs TOEFL gratuits — par niveau',
  mockTestHubIntro: 'Chaque session suit les paramètres opérationnels actuels pour le nombre de questions et la composition ; ce n’est pas le format officiel actuel exact, avec analyse IA.',
  mockTestHubWhyTitle: 'Pourquoi les examens blancs par niveau Power TOEFL',
  mockTestHubWhy1: 'L’IA génère de nouvelles questions chaque semaine — format TOEFL iBT actuel',
  mockTestHubWhy2: 'Trois niveaux adaptés à votre score cible',
  mockTestHubWhy3: 'Guide d’étude par session — contenu riche',
  mockTestHubWhy4: 'Carnet d’erreurs + analyse Reading·Listening',
  mockTestHubHowTitle: 'Comment utiliser',
  mockTestHubHow1: '1️⃣ Choisissez l’onglet de niveau adapté',
  mockTestHubHow2: '2️⃣ Lisez le guide de la session puis commencez',
  mockTestHubHow3: '3️⃣ Après le test, consultez l’analyse IA',
  mockTestHubHow4: '4️⃣ Répétez avec d’autres sessions du même niveau',
  mockTestHubFaq1Q: 'Combien de questions par session ?',
  mockTestHubFaq1A: 'Le nombre de questions est défini par session. Consultez la page de détail pour le nombre exact. Speaking·Writing sur des pages séparées.',
  mockTestHubFaq2Q: 'Combien de sessions gratuites ?',
  mockTestHubFaq2A: 'Toutes gratuites avec limite quotidienne. Pro illimité.',
  mockTestHubFaq3Q: 'Comment sont définis les niveaux ?',
  mockTestHubFaq3A: 'Débutant (15/section), intermédiaire (22), avancé (28).',
  mockTestHubFaq4Q: 'Où voir les résultats ?',
  mockTestHubFaq4A: 'Après envoi : score estimé et précision par section. Connexion pour sauvegarder.',
  mockTestHubAllRounds: 'Toutes les sessions',
};

const my: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'စာမေးပွဲစာရင်း',
  roundDetailLearningPoints: 'ဤအချိ၏ သင်ယူမှတ်သားရမည့်အချက်များ',
  roundDetailRelatedRounds: 'ဆင်တူအဆင့်အချိများ',
  roundDetailBackToHub: '← စာမေးပွဲစာရင်းသို့',
  recTabTitle: 'အကြံပြုအချိများ',
  recBeginner: 'အခြေခံ (ပိုင်း 15 ပစ်မှတ်)',
  recIntermediate: 'အလယ် (ပိုင်း 22 ပစ်မှတ်)',
  recAdvanced: 'အဆင့်မြင့် (ပိုင်း 28 ပစ်မှတ်)',
  recScore300: 'စုစုပေါင်း 40–70',
  recScore500: 'စုစုပေါင်း 70–95',
  recScore700: 'စုစုပေါင်း 95–120',
  recFullMock: 'Reading + Listening အပြည့်အစုံ',
  recTrap: 'ချည်ကြိုးမေးခွန်းများ',
  recWeakness: 'အားနည်းချက်ပြင်ဆင်',
  recRecommended: 'နောက်ဆုံးအကြံပြု',
  recPointSuffix: '/30',
  recQuestionSuffix: ' မေးခွန်း',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'TOEFL အခမဲ့ — အဆင့်အလိုက် | Power TOEFL',
  mockTestHubDesc: 'TOEFL iBT ကျွမ်းကျင်မှုကို Power TOEFL အဆင့်အလိုက်လေ့ကျင့်ခန်းများဖြင့် လေ့ကျင့်ပါ။ မေးခွန်းအရေအတွက်နှင့် ဖွဲ့စည်းပုံကို လက်ရှိလုပ်ငန်းဆောင်ရွက်မှု settings အတိုင်း ပံ့ပိုးပေးပြီး AI ရှင်းလင်းချက်ပါသည်။',
  mockTestHubH1: '🎯 TOEFL အခမဲ့ — အဆင့်အလိုက်အချိများ',
  mockTestHubIntro: 'တစ်ကြိမ်စီ၏ မေးခွန်းအရေအတွက်နှင့် ဖွဲ့စည်းပုံကို လက်ရှိလုပ်ငန်းဆောင်ရွက်မှု settings အတိုင်း ပံ့ပိုးပေးပါသည်။ လက်ရှိတရားဝင်ပုံစံအတိအကျမဟုတ်ဘဲ AI ခွဲခြမ်းစိတ်ဖြာမှုပါသည်။',
  mockTestHubWhyTitle: 'Power TOEFL အဆင့်ခွဲ mock test ဘာကြောင့်ကောင်း',
  mockTestHubWhy1: 'AI က အပတ်စဉ် မေးခွန်းအသစ်များ ထုတ်ပေးသည်',
  mockTestHubWhy2: 'ပစ်မှတ်ရမှတ်နှင့် ကိုက်ည하는 ၃ ဆင့်',
  mockTestHubWhy3: 'အချိတိုင်း သင်ယူမှုလမ်းညွှန်',
  mockTestHubWhy4: 'မှားယွင်းမှတ်တမ်း + Reading·Listening ခွဲခြမ်းစိတ်ဖြာ',
  mockTestHubHowTitle: 'အသုံးပြုနည်း',
  mockTestHubHow1: '1️⃣ ပစ်မှတ်နှင့် ကိုက်ည하는 အဆင့်ကို ရွေးပါ',
  mockTestHubHow2: '2️⃣ အချိအသေးစိတ်တွင် လမ်းညွှန်ဖတ်ပြီး စတင်ပါ',
  mockTestHubHow3: '3️⃣ စာမေးပွဲပြီးနောက် AI ခွဲခြမ်းစိတ်ဖြာမှု ကြည့်ပါ',
  mockTestHubHow4: '4️⃣ တူညီအဆင့် အခြားအချိများဖြင့် ထပ်လေ့ကျင့်ပါ',
  mockTestHubFaq1Q: 'တစ်ကြိမ်စီ မေးခွန်း ဘယ်နှစ်ခု?',
  mockTestHubFaq1A: 'မေးခွန်းအရေအတွက်ကို အချင်းချင်းတစ်ခုချင်းစီအလိုက် သတ်မှတ်ပေးထားပါသည်။ တိကျသော အရေအတွက်ကို အချင်းအသေးစိတ်စာမျက်နှာတွင် ကြည့်နိုင်ပြီး Speaking·Writing သည် သီးခြားစာမျက်နှာတွင် ရှိပါသည်။',
  mockTestHubFaq2Q: 'အခမဲ့ လေ့ကျင့်နိုင်သော အချိ ဘယ်နှစ်ကြိမ်?',
  mockTestHubFaq2A: 'အားလုံးအခမဲ့၊ နေ့စဉ်ကန့်သတ်ရှိသည်။ Pro မှာ ကန့်သတ်မရှိပါ။',
  mockTestHubFaq3Q: 'အဆင့်ခွဲခြင်းကို ဘယ်လို သတ်မှတ်ထားသလဲ?',
  mockTestHubFaq3A: 'အခြေခံ (15/ပိုင်း)၊ အလယ် (22)၊ အဆင့်မြင့် (28)။',
  mockTestHubFaq4Q: 'ရလဒ်ကို ဘယ်မှာ ကြည့်မလဲ?',
  mockTestHubFaq4A: 'တင်ပြီးနောက် ခန့်မှန်းရမှတ်နှင့် တိကျမှုကို ကြည့်ပါ။',
  mockTestHubAllRounds: 'အချိအားလုံး',
};

const ar: TierSeoKeys = {
  roundDetailBreadcrumbMockTest: 'قائمة الاختبارات التجريبية',
  roundDetailLearningPoints: 'نقاط التعلم لهذه الجولة',
  roundDetailRelatedRounds: 'جولات بمستوى مشابه',
  roundDetailBackToHub: '← العودة إلى قائمة الاختبارات',
  recTabTitle: 'جولات موصى بها',
  recBeginner: 'مبتدئ (هدف 15/30 لكل قسم)',
  recIntermediate: 'متوسط (هدف 22/30)',
  recAdvanced: 'متقدم (هدف 28/30)',
  recScore300: 'المجموع المستهدف 40–70',
  recScore500: 'المجموع المستهدف 70–95',
  recScore700: 'المجموع المستهدف 95–120',
  recFullMock: 'مجموعة Reading + Listening كاملة',
  recTrap: 'أسئلة الفخ',
  recWeakness: 'تعزيز نقاط الضعف',
  recRecommended: 'الأحدث الموصى بها',
  recPointSuffix: '/30',
  recQuestionSuffix: ' سؤالاً',
  recPartLabels: 'Reading,Conversations,Lectures',
  mockTestHubTitle: 'اختبارات TOEFL تجريبية مجانية — حسب المستوى | Power TOEFL',
  mockTestHubDesc: 'تدرّب على مهارات TOEFL iBT حسب المستوى عبر جولات Power TOEFL. عدد الأسئلة وتكوينها يتبع إعدادات التشغيل الحالية، مع شروحات بالذكاء الاصطناعي.',
  mockTestHubH1: '🎯 اختبارات TOEFL مجانية — حسب المستوى',
  mockTestHubIntro: 'كل جولة تتبع إعدادات التشغيل الحالية لعدد الأسئلة والتكوين، وليست الصيغة الرسمية الحالية المطابقة، مع تحليل بالذكاء الاصطناعي.',
  mockTestHubWhyTitle: 'لماذا الاختبارات التجريبية المتدرجة من Power TOEFL',
  mockTestHubWhy1: 'الذكاء الاصطناعي يولّد أسئلة جديدة أسبوعياً — أحدث صيغة TOEFL iBT',
  mockTestHubWhy2: 'ثلاثة مستويات تناسب درجتك المستهدفة',
  mockTestHubWhy3: 'دليل دراسة لكل جولة — محتوى غني',
  mockTestHubWhy4: 'دفتر الأخطاء + تحليل Reading·Listening',
  mockTestHubHowTitle: 'كيفية الاستخدام',
  mockTestHubHow1: '1️⃣ اختر تبويب المستوى المناسب لهدفك',
  mockTestHubHow2: '2️⃣ اقرأ دليل الجولة ثم ابدأ الاختبار',
  mockTestHubHow3: '3️⃣ بعد الاختبار راجع تحليل الذكاء الاصطناعي',
  mockTestHubHow4: '4️⃣ كرّر مع جولات أخرى من نفس المستوى',
  mockTestHubFaq1Q: 'كم سؤالاً في كل جولة؟',
  mockTestHubFaq1A: 'يتم تحديد عدد الأسئلة لكل جولة. راجع صفحة تفاصيل الجولة لمعرفة العدد الدقيق. تتوفر Speaking·Writing في صفحات منفصلة.',
  mockTestHubFaq2Q: 'كم جولة مجاناً؟',
  mockTestHubFaq2A: 'الكل مجاني مع حد يومي. Pro بلا حدود.',
  mockTestHubFaq3Q: 'كيف تُعرّف المستويات؟',
  mockTestHubFaq3A: 'مبتدئ (15/قسم)، متوسط (22)، متقدم (28).',
  mockTestHubFaq4Q: 'أين أرى النتائج؟',
  mockTestHubFaq4A: 'بعد الإرسال: الدرجة التقديرية ودقة كل قسم. سجّل الدخول للحفظ.',
  mockTestHubAllRounds: 'جميع الجولات',
};

/** ko and en keep strings in ko.ts / en.ts — empty override here. */
export const tierSeoI18n: Partial<Record<TierSeoLang, TierSeoKeys>> = {
  ja,
  th,
  vi,
  id,
  'zh-TW': zhTW,
  'zh-CN': zhCN,
  ms,
  'pt-BR': ptBR,
  de,
  es,
  fr,
  my,
  ar,
};

export function applyTierSeoI18n<T extends Record<string, unknown>>(base: T, lang: TierSeoLang | 'ko' | 'en'): T {
  if (lang === 'ko' || lang === 'en') return base;
  const patch = tierSeoI18n[lang];
  if (!patch) return base;
  return { ...base, ...patch };
}
